import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight, Film, CalendarDays, MapPin } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { api, resolveImg } from "@/lib/api";
import { fmtJourneyDate, experiencePlace } from "@/lib/content";

export default function FlagshipSpotlight() {
  const { data: journeys = [] } = useQuery({
    queryKey: ["journeys"],
    queryFn: async () => (await api.get("/journeys")).data,
  });
  const f = journeys.find((x) => x.is_flagship && ["OPEN", "UPCOMING"].includes(x.status));
  if (!f) return null;
  const isLastHorizon = f.title === "The Last Horizon";

  const cover = f.images?.[0];
  const place = experiencePlace(f);

  return (
    <section data-testid="flagship-spotlight" className="relative bg-ptf-text text-ptf-bg overflow-hidden grain">
      {cover && (
        <>
          <img src={resolveImg(cover)} alt={f.title} className="absolute inset-0 h-full w-full object-cover opacity-35" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-r from-ptf-text via-ptf-text/80 to-ptf-text/30" />
        </>
      )}
      <div className="relative z-10 mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 py-24 md:py-36">
        <Reveal>
          <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-accent mb-6 flex items-center gap-3">
            <Film className="h-4 w-4" /> {isLastHorizon ? "Planet Trotter Wildlife Filmmaking Challenge" : "Planet Trotter Field Experience · Documentary Expedition"}
          </p>
          <h2 className="font-display font-extrabold uppercase text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.92] max-w-3xl">
            {f.title}
          </h2>
          {f.summary && <p className="mt-6 max-w-2xl text-ptf-bg/80 leading-relaxed">{f.summary}</p>}

          <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-3">
            {place && (
              <span className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.14em] text-ptf-bg/85">
                <MapPin className="h-4 w-4 text-ptf-accent" /> {place}
              </span>
            )}
            {f.start_date && (
              <span className="inline-flex items-center gap-2 text-sm text-ptf-bg/85">
                <CalendarDays className="h-4 w-4 text-ptf-accent" />
                {fmtJourneyDate(f.start_date)}{f.end_date ? ` to ${fmtJourneyDate(f.end_date)}` : ""}
              </span>
            )}
            {f.duration_days > 0 && <span className="text-sm text-ptf-bg/85">{f.duration_days} days</span>}
            {isLastHorizon ? (
              <span data-testid="spotlight-participation" className="text-sm text-ptf-bg/70 uppercase tracking-[0.12em]">6 filmmakers selected by jury</span>
            ) : f.seats_total > 0 && (
              <span data-testid="spotlight-participation" className="text-sm text-ptf-bg/70 uppercase tracking-[0.12em]">Limited participation</span>
            )}
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            {isLastHorizon ? (
              <Link
                to="/last-horizon/apply"
                data-testid="spotlight-apply-cta"
                className="inline-flex items-center justify-center gap-2 bg-ptf-accent text-ptf-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-bg transition-colors"
              >
                Apply for The Last Horizon <ArrowUpRight className="h-4 w-4" />
              </Link>
            ) : (
              <Link
                to={`/exploration/${f.id}`}
                data-testid="spotlight-view-cta"
                className="inline-flex items-center justify-center gap-2 bg-ptf-accent text-ptf-primary px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-bg transition-colors"
              >
                Explore the Expedition <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
            <Link
              to={isLastHorizon ? `/exploration/${f.id}` : "/flagship"}
              data-testid="spotlight-flagship-cta"
              className="inline-flex items-center justify-center border border-ptf-bg/40 text-ptf-bg px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] hover:bg-ptf-bg hover:text-ptf-primary transition-colors"
            >
              {isLastHorizon ? "See The Full Itinerary" : "How Flagships Work"}
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
